import { createI18n } from 'vue-i18n'
import type { I18n, I18nOptions } from 'vue-i18n'
import { LanguageConfig } from './types/i18n'

// Language configurations
export const languages: LanguageConfig[] = [
  { code: "en", name: "English", dir: "ltr", flag: "🇺🇸", currency: "USD" },
  { code: "ar", name: "العربية", dir: "rtl", flag: "🇸🇦", currency: "AED" },
];

// Import translations
const translationLoaders = {
  en: {
    common: () => import("./en/common").then((m) => m.default),
    navigation: () => import("./en/navigation").then((m) => m.default),
    forms: () => import("./en/forms").then((m) => m.default),
    social: () => import("./en/social").then((m) => m.default),
    stocks: () => import("./en/stocks").then((m) => m.default),
    profileDetails: () => import("./en/profileDetails").then((m) => m.default),
    trading: () => import("./en/trading").then((m) => m.default),
    errors: () => import("./en/errors").then((m) => m.default),
    validation: () => import("./en/validation").then((m) => m.default),
    filters: () => import("./en/filters").then((m) => m.default),
    settings: () => import("./en/settings").then((m) => m.default),
    auth: () => import("./en/auth").then((m) => m.default),
    privacy: () => import("./en/privacy").then((m) => m.default),
    explore: () => import("./en/explore").then((m) => m.default),
    circles: () => import("./en/circles").then((m) => m.default),
    news: () => import("./en/news").then((m) => m.default),
    notification: () => import("./en/notification").then((m) => m.default),
    markets: () => import("./en/markets").then((m) => m.default),
    profile: () => import("./en/profile").then((m) => m.default),
    portfolio: () => import("./en/portfolio").then((m) => m.default),
    setting: () => import("./en/setting").then((m) => m.default),
    analysis: () => import("./en/analysis").then((m) => m.default),
    alpha: () => import("./en/alpha").then((m) => m.default),
    report: () => import("./en/report").then((m) => m.default),
  },
  ar: {
    common: () => import("./ar/common").then((m) => m.default),
    navigation: () => import("./ar/navigation").then((m) => m.default),
    forms: () => import("./ar/forms").then((m) => m.default),
    social: () => import("./ar/social").then((m) => m.default),
    stocks: () => import("./ar/stocks").then((m) => m.default),
    profileDetails: () => import("./ar/profileDetails").then((m) => m.default),
    trading: () => import("./ar/trading").then((m) => m.default),
    errors: () => import("./ar/errors").then((m) => m.default),
    validation: () => import("./ar/validation").then((m) => m.default),
    filters: () => import("./ar/filters").then((m) => m.default),
    settings: () => import("./ar/settings").then((m) => m.default),
    auth: () => import("./ar/auth").then((m) => m.default),
    privacy: () => import("./ar/privacy").then((m) => m.default),
    explore: () => import("./ar/explore").then((m) => m.default),
    circles: () => import("./ar/circles").then((m) => m.default),
    news: () => import("./ar/news").then((m) => m.default),
    notification: () => import("./ar/notification").then((m) => m.default),
    markets: () => import("./ar/markets").then((m) => m.default),
    profile: () => import("./ar/profile").then((m) => m.default),
    portfolio: () => import("./ar/portfolio").then((m) => m.default),
    setting: () => import("./ar/setting").then((m) => m.default),
    analysis: () => import("./ar/analysis").then((m) => m.default),
    alpha: () => import("./ar/alpha").then((m) => m.default),
    report: () => import("./ar/report").then((m) => m.default),
  },
};

// Load messages function
async function loadLocaleMessages(locale: string) {
  const loaders = translationLoaders[locale as keyof typeof translationLoaders];
  if (!loaders) {
    console.warn(`No translation loaders found for locale: ${locale}`);
    return {};
  }

  const messages: any = {};
  for (const [key, loader] of Object.entries(loaders)) {
    try {
      messages[key] = await loader();
    } catch (error) {
      console.error(`Failed to load ${key} translations for ${locale}:`, error);
    }
  }
  return messages;
}

// Create i18n instance with empty messages initially
export const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  globalInjection: true,
  messages: {},
  missing: (locale, key) => {
    console.warn(`i18n missing translation: ${key} for locale: ${locale}`);
    return key;
  }
})

// Initialize function
export async function setupI18n() {
  try {
    const locale = getPersistedLocale();
    // console.log(`Loading messages for locale: ${locale}`);

    const messages = await loadLocaleMessages(locale);

    // Set the messages for the locale
    i18n.global.setLocaleMessage(locale, messages);
    i18n.global.locale.value = locale;

    // console.log(`i18n initialized with locale: ${locale}`);
    // console.log('Available namespaces:', Object.keys(messages));

    // Set HTML attributes
    updateDocumentAttributes(locale);

    return i18n;
  } catch (error) {
    console.error('Failed to setup i18n:', error);
    // Return the i18n instance even if loading fails
    return i18n;
  }
}

const getSupportedLanguage = () => {
  const supportedLanguages = languages.map(lang => lang.code)
  const preferredLanguages = navigator.languages;

  for (const preferredLang of preferredLanguages) {
    if (supportedLanguages.includes(preferredLang)) {
      return preferredLang;
    }
    const languageCode = preferredLang.split("-")[0];
    if (supportedLanguages.includes(languageCode)) {
      return languageCode;
    }
  }

  return "en";
}

// Utility functions
export function getPersistedLocale(): string {
  return localStorage.getItem('preferred-language') || getSupportedLanguage() || 'en';
}

export function setPersistedLocale(locale: string): void {
  localStorage.setItem('preferred-language', locale);
  updateDocumentAttributes(locale);
}

export function updateDocumentAttributes(locale: string): void {
  const language = languages.find(lang => lang.code === locale);
  // console.log(`Updating document attributes for locale: ${locale}`, language);
  if (language) {
    // document.documentElement.lang = locale;
    document.documentElement.dir = language.dir;
    //  // Reload to apply direction changes

    
    // Set input mode for better mobile keyboard support
    if (language.dir === 'rtl') {
      document.documentElement.setAttribute('inputmode', 'text')
    }

    // console.log(`Document attributes updated: lang=${locale}, dir=${language.dir}`);
  }
}

export const getCurrency = () => {
  const language = languages.find((lang) => lang.code === getPersistedLocale());
  return language.currency
}

export const getDirection = (locale: string) => {
  const language = languages.find((lang) => lang.code === locale);
  return language.dir;
};

export default i18n;